import { NotificationTypeEnum } from '@acceso/design-system';
import { AxiosProgressEvent } from 'axios';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getFileNameFromURI } from '@/features/segments/utils';
import { useNotificationStore } from '@/stores/notifications';
import { FileUploadState } from '@/types/index';

import { useDeleteCNMCFile } from '../api/delete-cnmc-file';
import { uploadFileToCNMC, TUploadFileToCNMCParams } from '../api/post-cnmc-file';
import { uploadFileChunk, TUploadFileChunkResponse } from '../api/post-cnmc-file-chunk';
import { completeMultipartUpload } from '../api/post-cnmc-file-complete';
import { initMultipartUpload } from '../api/post-cnmc-file-init';
import { useCNMCFileUploadStore, CNMCFileUpload } from '../stores/cnmc-files-upload-store';
import { CNMCFile, CNMCFileStateEnum } from '../types';

const PROGRESS_NOTIFICATION_ID = 1;

const DIRECT_UPLOAD_MAX_SIZE = 100 * 1024 * 1024; // 100 MB
const CHUNK_SIZE = 25 * 1024 * 1024; // 25 MB

export function useCNMCFileUploadsController() {
  const { t } = useTranslation();
  const {
    addErrorNotification,
    addSuccessNotification,
    addNotificationId,
    dismissNotification,
    updateNotification,
  } = useNotificationStore();

  const {
    files,
    addFile: addFileToStore,
    removeFile: removeFileFromStore,
    updateFileStatus,
    setFileBackendId,
  } = useCNMCFileUploadStore();

  const { mutate: deleteCNMCFile } = useDeleteCNMCFile();

  const batchNotificationSentRef = useRef(false);

  const updateProgressNotification = (shouldCreateNotification: boolean) => {
    const currentFiles = useCNMCFileUploadStore.getState().files;
    const newFiles = currentFiles.filter(file => file.isNewUpload);
    const completedNewFiles = newFiles.filter(
      file => file.status === FileUploadState.FINISHED || file.status === FileUploadState.ERROR
    );

    if (currentFiles.length === 0) {
      dismissNotification(PROGRESS_NOTIFICATION_ID);
      return;
    }

    if (newFiles.length === 0) {
      dismissNotification(PROGRESS_NOTIFICATION_ID);
      return;
    }

    const message = `${completedNewFiles.length}/${newFiles.length}`;
    const notificationExists = useNotificationStore
      .getState()
      .notifications.some(n => n.id === PROGRESS_NOTIFICATION_ID);

    if (!notificationExists) {
      if (!shouldCreateNotification) {
        return;
      }
      addNotificationId({
        id: PROGRESS_NOTIFICATION_ID,
        type: NotificationTypeEnum.INFO,
        title: t('Subiendo ficheros'),
        message: message,
        infinite: true,
      });
    } else {
      updateNotification(PROGRESS_NOTIFICATION_ID, { message });
    }
  };

  const addFile = (cnmcFile: CNMCFile) => {
    const isAlreadyAdded = files.some(file => file.backendFileId === cnmcFile.id);

    if (isAlreadyAdded) {
      return;
    }

    const fileUpload: CNMCFileUpload = {
      cnmcId: cnmcFile.cnmc_id,
      file: cnmcFile.presigned_url,
      id: cnmcFile.id,
      name: getFileNameFromURI(cnmcFile.uri),
      status:
        cnmcFile.state === CNMCFileStateEnum.UPLOADING
          ? FileUploadState.IN_PROGRESS
          : FileUploadState.FINISHED,
      backendFileId: cnmcFile.id,
      isNewUpload: false,
    };
    addFileToStore(fileUpload);
  };

  const uploadFile = async (
    file: File,
    cnmcId: string,
    currentFileIndex: number,
    totalFiles: number
  ) => {
    const fileId = crypto.randomUUID();

    const controller = new AbortController();
    const signal = controller.signal;

    const newFile: CNMCFileUpload = {
      id: fileId,
      file,
      name: file.name,
      status: FileUploadState.IN_PROGRESS,
      cnmcId,
      controller,
      backendFileId: null,
      isNewUpload: true,
    };

    addFileToStore(newFile);

    batchNotificationSentRef.current = false;
    updateProgressNotification(true);

    const uploadParams: TUploadFileToCNMCParams = {
      cnmcId: newFile.cnmcId,
      file,
      signal,
      onUploadProgress: (event: AxiosProgressEvent) => {
        try {
          const total = typeof event.total === 'number' ? event.total : undefined;
          const percent =
            total && typeof event.loaded === 'number'
              ? Math.round((event.loaded / total) * 100)
              : undefined;

          const parts = [`${currentFileIndex + 1}/${totalFiles} ${newFile.name}`];
          if (percent !== undefined) parts.push(`${percent}%`);
          updateNotification(PROGRESS_NOTIFICATION_ID, { message: parts.join(' - ') });
        } catch {}
      },
    };

    try {
      let uploadedBackendFile: CNMCFile;

      if (file.size <= DIRECT_UPLOAD_MAX_SIZE) {
        uploadedBackendFile = await uploadFileToCNMC(uploadParams);
      } else {
        console.log(file);
        const initResponse = await initMultipartUpload({
          cnmcId: newFile.cnmcId,
          filename: file.name,
          mimetype: file.type || 'video/x-matroska',
        });

        setFileBackendId(newFile.id, initResponse.id);
        uploadedBackendFile = initResponse;

        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const etags: TUploadFileChunkResponse[] = [];

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          const partNumber = i + 1;

          const chunkResponse = await uploadFileChunk({
            cnmcId: newFile.cnmcId,
            fileId: uploadedBackendFile.id,
            file: chunk,
            partNumber,
            signal,
            onUploadProgress: (event: AxiosProgressEvent) => {
              try {
                const chunkTotal = typeof event.total === 'number' ? event.total : end - start;
                const chunkLoaded = typeof event.loaded === 'number' ? event.loaded : 0;

                let percent: number | undefined = undefined;
                if (chunkTotal > 0) {
                  const chunkFraction = chunkLoaded / chunkTotal;
                  const overallFraction = (i + chunkFraction) / totalChunks;
                  percent = Math.floor(overallFraction * 100);
                }

                const parts = [`${currentFileIndex + 1}/${totalFiles} ${newFile.name}`];
                if (percent !== undefined) parts.push(`${percent}%`);
                updateNotification(PROGRESS_NOTIFICATION_ID, { message: parts.join(' - ') });
              } catch {}
            },
          });
          etags.push(chunkResponse);
        }

        await completeMultipartUpload({
          cnmcId: newFile.cnmcId,
          fileId: uploadedBackendFile.id,
        });
      }

      updateFileStatus(newFile.id, FileUploadState.FINISHED);
      setFileBackendId(newFile.id, uploadedBackendFile.id);
    } catch (error: any) {
      updateFileStatus(newFile.id, FileUploadState.ERROR, error.message);
    } finally {
      useCNMCFileUploadStore.setState(state => ({
        files: state.files.map(file => {
          if (file.id === newFile.id) {
            return {
              ...file,
              controller: undefined,
            };
          }
          return file;
        }),
      }));

      updateProgressNotification(true);

      const currentFiles = useCNMCFileUploadStore.getState().files;
      const newFiles = currentFiles.filter(file => file.isNewUpload);
      const allNewFilesProcessed = newFiles.every(
        file => file.status === FileUploadState.FINISHED || file.status === FileUploadState.ERROR
      );

      if (allNewFilesProcessed && !batchNotificationSentRef.current) {
        const anyErrors = newFiles.some(file => file.status === FileUploadState.ERROR);

        dismissNotification(PROGRESS_NOTIFICATION_ID);

        if (anyErrors) {
          addErrorNotification(
            t('Los ficheros no se han cargado correctamente'),
            t('Ha habido un error en el proceso')
          );
        } else {
          addSuccessNotification(
            t('Los ficheros se han cargado corectamente'),
            t('El proceso ha sido un éxito')
          );
        }
        batchNotificationSentRef.current = true;
      }
    }
  };
  const removeFile = (fileId: string) => {
    const fileToRemove = files.find(file => file.id === fileId);

    if (fileToRemove) {
      fileToRemove.controller?.abort();

      if (fileToRemove.status === FileUploadState.FINISHED && fileToRemove.backendFileId) {
        deleteCNMCFile(
          { cnmcId: fileToRemove.cnmcId, fileId: fileToRemove.backendFileId },
          {
            onSuccess: () => {
              addSuccessNotification(t('El archivo se ha eliminado correctamente'));
            },
            onError: () => {
              addErrorNotification(t('No se ha podido eliminar el archivo'));
            },
          }
        );
      }
    }
    removeFileFromStore(fileId);
    updateProgressNotification(false);
  };

  return {
    uploadFile,
    removeFile,
    addFile,
  };
}
